import { AxiosError, AxiosResponse } from "axios";
import { IGetListRequest, IResponse, IServerError } from "../models/common";

import http from "../common/http-commons";

export interface ApiErrorResponse {
  status: false;
  message: string;
  error?: boolean;
}
const MESSAGES = {
  unauthorized: "Unauthorized",
  serverError: "Something went wrong.",
  generic: "Something went wrong. Please try again later.",
  badRequest: "Bad Request.",
} as const;

const format422Errors = (data: unknown): string => {
  const errors = (data as any)?.errors ?? data;

  if (!errors || typeof errors !== "object") {
    return "Validation failed.";
  }

  return Object.entries(errors)
    .map(([field, messages]) => {
      const text = Array.isArray(messages)
        ? messages.join(", ")
        : String(messages);
      return `${field}: ${text}`;
    })
    .join("\n");
};

const extractErrorMessage = (error: AxiosError): string => {
  const data = error.response?.data as any;

  if (typeof data === "string" && data.trim().length > 0) return data;

  if (data && typeof data === "object") {
    if (typeof data.message === "string") return data.message;
    if (typeof data.error === "string") return data.error;
  }

  return error.message || "Unknown error";
};
const handleRequestError = (
  error: any,
  navigateToLogout?: (path: string) => void,
): ApiErrorResponse => {
  const axiosError = error as AxiosError;
  //   console.error("[CommonService] Request failed:", axiosError);

  const status = axiosError.response?.status;

  if (status === 401) {
    navigateToLogout?.("/logout");
    return { status: false, message: MESSAGES.unauthorized };
  }

  if (status === 422) {
    return {
      status: false,
      message: `Errors:\n${format422Errors(axiosError.response?.data)}`,
    };
  }

  return { status: false, message: extractErrorMessage(axiosError) };
};

export const CommonService = {
  getList: async (
    url: string,
    navigateToLogout?: (path: string) => void,
  ): Promise<any | IServerError | IResponse | null> => {
    if (!url) return null;
    try {
      const response: AxiosResponse = await http.get<any>(url);
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return { status: false, message: MESSAGES.serverError, error: true };
      }
      return { status: false, message: MESSAGES.generic };
    } catch (error) {
      return handleRequestError(error, navigateToLogout);
    }
  },
  getById: async (
    url: string | undefined,
    navigateToLogout?: (path: string) => void,
  ): Promise<IServerError | IResponse | null | any> => {
    if (!url) return null;
    try {
      const response: AxiosResponse<any> = await http.get<any>(url);
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return { status: false, message: MESSAGES.serverError, error: true };
      }
      return { status: false, message: MESSAGES.generic };
    } catch (error) {
      return handleRequestError(error, navigateToLogout);
    }
  },
  create: async (
    createUrl: string,
    updateUrl: string,
    requestPayload: any,
    navigateToLogout?: (path: string) => void,
  ): Promise<IServerError | IResponse | any> => {
    if (!createUrl) return null;
    if (requestPayload.id) {
      if (!updateUrl) return null;
      console.log("requestPayload.id", requestPayload.id);
      try {
        const response: AxiosResponse<any> = await http.put<any>(
          updateUrl,
          requestPayload,
        );

        if (response.status === 200) {
          return response;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }
      } catch (error) {
        return handleRequestError(error, navigateToLogout);
      }
    }

    try {
      const response: AxiosResponse<any> = await http.post<any>(
        createUrl,
        requestPayload,
      );

      if (response.status === 201) {
        return response;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return { status: false, message: MESSAGES.serverError, error: true };
      }
      return { status: false, message: MESSAGES.generic };
    } catch (error) {
      return handleRequestError(error, navigateToLogout);
    }
  },
  deleteById: async (
    url: string,
    navigateToLogout?: (path: string) => void,
  ): Promise<string | IServerError | IResponse | any> => {
    if (!url) return null;
    try {
      const response: AxiosResponse<string> = await http.delete<string>(url);

      if (response.status === 204) {
        return "Success";
      } else if (response.status === 200) {
        return "Success";
      } else if (response.status === 500) {
        return { status: false, message: MESSAGES.serverError, error: true };
      }
      return { status: false, message: MESSAGES.generic };
    } catch (error) {
      return handleRequestError(error, navigateToLogout);
    }
  },
  bulkDelete: async (
    url: string,
    requestPayload: any,
    navigateToLogout?: (path: string) => void,
  ): Promise<any | IServerError | IResponse> => {
    if (!url) return null;
    try {
      const response = await http.delete<string>(url, {
        data: requestPayload.Ids,
      });
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return { status: false, message: MESSAGES.serverError, error: true };
      }

      return { status: false, message: MESSAGES.generic };
    } catch (error) {
      return handleRequestError(error, navigateToLogout);
    }
  },
  getDDL: async (
    url: string,
    navigateToLogout?: (path: string) => void,
  ): Promise<any | IServerError | IResponse | null> => {
    if (!url) return null;
    try {
      const response: AxiosResponse<any[]> = await http.get<any[]>(url);

      if (response.status === 200) {
        return response.data;
      }

      return { status: false, message: MESSAGES.generic };
    } catch (error) {
      return handleRequestError(error, navigateToLogout);
    }
  },
  archive: async (
    url: string,
    archivePayload: any,
    navigateToLogout?: (path: string) => void,
  ): Promise<string | IServerError | IResponse | any> => {
    if (!url) return null;
    try {
      const response: AxiosResponse<string> = await http.post<string>(
        url,
        archivePayload,
      );
      if (response.status === 200) {
        return response.data;
      } else if (response.status === 201) {
        return response.data;
      } else if (response.status === 500) {
        return { status: false, message: MESSAGES.serverError, error: true };
      }
      return { status: false, message: MESSAGES.generic };
    } catch (error) {
      return handleRequestError(error, navigateToLogout);
    }
  },
};
